import { isNative } from '../services/platform/platformDetector';
import storageService from '../services/platform/storageService';
import { prerenderTikzInHtml } from './tikzRender';
import { cacheSvg, postSvgCacheToServer } from './tikzSvgCache';

const LOGO_PATH = 'https://customer-assets.emergentagent.com/job_821d8d9c-247d-4a2e-aa6e-162e83b2fa1e/artifacts/pprgtqcj_EDSUPP%20Logo.jpg';

const formatTopics = (topics) => {
  if (!topics) return 'All Topics';
  if (Array.isArray(topics)) {
    const topicsStr = topics.join(', ');
    const words = topicsStr.split(' ');
    if (words.length > 12) {
      return words.slice(0, 12).join(' ') + ' …';
    }
    return topicsStr;
  }
  return topics;
};

const sanitizeFileName = (name) =>
  (name || 'worksheet')
    .replace(/[^a-z0-9-_\s]/gi, '')
    .trim()
    .replace(/\s+/g, '-');

const wait = (ms = 0) => new Promise(resolve => setTimeout(resolve, ms));

// Render every <script type="text/tikz"> in the content into inline SVG
// before the PDF/print path sees it. html2pdf has no JS runtime, and the
// print-window TikZJax is a flaky race against window.print() — pre-rendering
// guarantees the export carries the diagrams.
//
// Best-effort: on failure (TikZJax unavailable, render timeout), the original
// HTML is returned unchanged so the existing in-page TikZJax fallback can
// still try, and the export proceeds without throwing.
const prerenderExportContent = async (options) => {
  const content = options?.content;
  if (typeof content !== 'string' || !content.includes('text/tikz')) {
    return content;
  }
  try {
    const seenHashes = new Set();
    const writeBackEntries = [];
    const { html: prerendered, rendered, failed } = await prerenderTikzInHtml(content, {
      onRendered: ({ tikzCode, svgHtml }) => {
        cacheSvg(tikzCode, svgHtml);
        // De-dupe before batching — exports often repeat the same diagram in
        // print+online HTML pulled from the same JSON.
        const dedupeKey = `${tikzCode.length}:${tikzCode.slice(0, 64)}`;
        if (!seenHashes.has(dedupeKey)) {
          seenHashes.add(dedupeKey);
          writeBackEntries.push({ tikzCode, svgHtml });
        }
      },
    });
    if (rendered > 0) {
      console.info(`[worksheetExport] pre-rendered ${rendered} TikZ diagrams (${failed} failed)`);
    }
    if (options?.worksheetId && writeBackEntries.length > 0) {
      // Fire-and-forget — export shouldn't block on the persist call.
      postSvgCacheToServer(options.worksheetId, writeBackEntries);
    }
    return prerendered;
  } catch (err) {
    console.warn('[worksheetExport] pre-render failed, falling back to in-page TikZJax:', err);
    return content;
  }
};

const waitForImagesToLoad = async (container, timeout = 12000) => {
  const images = Array.from(container.querySelectorAll('img'));
  if (images.length === 0) return;

  const loadPromises = images.map(img => {
    if (img.complete && img.naturalWidth !== 0) return Promise.resolve();

    return new Promise(resolve => {
      const finish = () => {
        img.removeEventListener('load', finish);
        img.removeEventListener('error', finish);
        resolve();
      };

      img.addEventListener('load', finish);
      img.addEventListener('error', finish);
      setTimeout(finish, timeout);
    });
  });

  await Promise.all(loadPromises);
};

export const buildWorksheetHTML = ({
  title,
  content,
  grade,
  subject,
  topics,
  date,
  taskCode,
  totalMarks,
  totalQuestions,
  answerKey,
  includeTikz = false,
}) => {
  const logoUrl = `${window.location.origin}${LOGO_PATH}`;
  const topicsStr = formatTopics(topics);
  const formattedDate = date || new Date().toLocaleDateString();
  const shouldShowTopics = typeof topics === 'string' ? topics.trim().length > 0 : Array.isArray(topics) ? topics.length > 0 : Boolean(topics);
  const shouldShowTaskCode = typeof taskCode === 'string' ? taskCode.trim().length > 0 : Boolean(taskCode);

  // Strip any server-prepended Task Code div from content — it duplicates the
  // header entry that buildWorksheetHTML renders below when shouldShowTaskCode is true.
  const cleanedContent = typeof content === 'string'
    ? content.replace(
        /^\s*<div\s+style="text-align:\s*center;\s*margin-bottom:\s*20px;\s*padding:\s*10px;\s*border:\s*2px\s*solid\s*#333;">\s*<h3>Task Code:\s*<strong>[^<]*<\/strong><\/h3>\s*<\/div>\s*/i,
        ''
      )
    : content;

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          /*
           * Force light-mode rendering.  When this HTML is inserted into the live
           * DOM (saveWorksheetAsPDF uses html2canvas which renders the page as-is),
           * the app's dark-mode CSS may otherwise cascade near-white text colours
           * into this subtree.  color-scheme:light resets UA-controlled colours and
           * the explicit color/background rules prevent inheritance from dark-mode
           * ancestors.  CSS custom properties used by worksheet content are also
           * reset here so var(--muted) etc. resolve to light-mode values.
           */
          :root, html, body {
            color-scheme: light;
            --text: #111;
            --muted: #444;
            --accent: #0a66c2;
            --border: #ddd;
          }
          body { font-family: Arial, sans-serif; margin: 20px; color: #111; background: #fff; }
          .export-shell {
            width: 195mm; padding: 5mm 10mm 12mm; margin: 0 auto; box-sizing: border-box;
            background: white; color: #111; color-scheme: light;
          }
          .export-header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
          .meta { font-size: 0.9em; color: #666; margin-top: 5px; }
          .content { line-height: 1.6; }
          .worksheet-content .marks { 
            color: #666; 
            font-weight: bold; 
            background: #f0f0f0;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 0.9em;
          }
          .answer-input input, .answer-input textarea { 
            width: 100% !important; 
            border: none !important; 
            border-bottom: 1px solid #000 !important; 
            background: transparent !important; 
            min-height: 2rem !important;
          }
          .answer-input input::placeholder, .answer-input textarea::placeholder { 
            color: transparent !important; 
          }
          .export-footer { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: center; gap: 8px; color: #6b7280; font-size: 12px; flex-wrap: wrap; text-align: center; }
          @media print { 
            body { margin: 0; } 
            .answer-input { background: white !important; }
          }
        </style>
      </head>
      <body>
        <div class="export-shell">
          <div class="export-header text-center">
            <h1 class="mb-4" style="font-size: 20px;"><strong>${title}</strong></h1>
            <p class="mb-2" style="color: #374151;"><strong>Date:</strong> ${formattedDate} | <strong>Grade:</strong> ${grade} | <strong>Subject:</strong> ${subject}</p>
            ${shouldShowTopics ? `<p class="mb-2" style="color: #374151;"><strong>Topics:</strong> ${topicsStr}</p>` : ''}
            ${shouldShowTaskCode ? `<p class="mb-2" style="color: #374151;"><strong>Task Code:</strong> ${taskCode}</p>` : ''}
          </div>

          <div class="content">
            ${cleanedContent || '<p>No content available</p>'}
          </div>

          <div class="export-footer">
            <span>Generated by Edsupp - Your Learning Companion</span>
          </div>
        </div>
        ${includeTikz ? '<script src="https://tikzjax.com/v1/tikzjax.js"></script>' : ''}
        <script defer src="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.js" crossorigin="anonymous"></script>
        <script defer src="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js" crossorigin="anonymous"></script>
        <script>
          document.addEventListener('DOMContentLoaded', function() {
            if (window.renderMathInElement) {
              try {
                window.renderMathInElement(document.body, {
                  delimiters: [
                    { left: '$$', right: '$$', display: true },
                    { left: '$', right: '$', display: false },
                    { left: '\\(', right: '\\)', display: false },
                    { left: '\\[', right: '\\]', display: true }
                  ]
                });
              } catch (e) {
                console.error('KaTeX render error:', e);
              }
            }
          });
        </script>
      </body>
    </html>
  `;
};

export const printWorksheet = (options) => {
  // window.open and window.print() are blocked in iOS WKWebView.
  // On native mobile, fall through to saveWorksheetAsPDF instead.
  if (isNative()) {
    return saveWorksheetAsPDF(options);
  }

  // Open the print window synchronously to preserve the user-gesture context
  // (popup blockers reject window.open from after-await code paths). Write a
  // loading placeholder, then pre-render TikZ in the *calling* window where
  // the singleton TikZJax lives, then inject the final HTML into the popup.
  const printWindow = window.open('', '_blank');
  if (!printWindow || !printWindow.document) {
    throw new Error('Unable to open print window. Please allow pop-ups and try again.');
  }
  printWindow.document.write(
    '<!DOCTYPE html><html><head><title>Preparing worksheet…</title>' +
    '<style>body{font-family:Arial,sans-serif;padding:40px;text-align:center;color:#374151}</style>' +
    '</head><body><p>Preparing worksheet for print…</p></body></html>'
  );
  printWindow.document.close();

  return prerenderExportContent(options)
    .then((prerenderedContent) => {
      const stillHasTikz =
        typeof prerenderedContent === 'string' && prerenderedContent.includes('text/tikz');
      const html = buildWorksheetHTML({
        ...options,
        content: prerenderedContent,
        includeTikz: stillHasTikz,
      });
      // Popup may have been closed by the user while we were rendering.
      if (printWindow.closed) return;
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      const delay = stillHasTikz ? 5500 : 1000;
      setTimeout(() => {
        if (printWindow.closed) return;
        printWindow.focus();
        printWindow.print();
      }, delay);
    })
    .catch((err) => {
      console.error('[printWorksheet] failed to prepare content:', err);
      if (!printWindow.closed) {
        try { printWindow.close(); } catch (_e) { /* ignore */ }
      }
      throw err;
    });
};

export const saveWorksheetAsPDF = async (options) => {
  // html2pdf has no JS runtime, so any unrendered TikZ scripts would emit as
  // blank space. Pre-render in the calling window first so the PDF actually
  // contains the diagrams.
  const prerenderedContent = await prerenderExportContent(options);
  const html = buildWorksheetHTML({ ...options, content: prerenderedContent });
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.left = '-10000px';
  wrapper.style.top = '0';
  wrapper.style.width = '210mm';
  wrapper.style.background = '#fff';
  // Prevent the app's dark-mode CSS from cascading light/near-white text
  // colours into this subtree — html2canvas renders the live DOM, so dark
  // mode affects the output if we don't explicitly opt out here.
  wrapper.style.colorScheme = 'light';
  wrapper.style.color = '#111';

  const container = document.createElement('div');
  container.innerHTML = html;
  container.style.width = '180mm';
  wrapper.appendChild(container);
  document.body.appendChild(wrapper);

  try {
    // Render KaTeX in the hidden container before exporting
    try {
      const { default: renderMathInElement } = await import('katex/dist/contrib/auto-render');
      renderMathInElement(container, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '$', right: '$', display: false },
          { left: '\\(', right: '\\)', display: false },
          { left: '\\[', right: '\\]', display: true }
        ]
      });
    } catch (mathErr) {
      console.error('KaTeX render (PDF) error:', mathErr);
    }

    const html2pdfModule = await import('html2pdf.js');
    const html2pdfInstance = html2pdfModule.default || html2pdfModule;
    const fileName = sanitizeFileName(options.fileName || options.title);

    // Ensure diagrams/images have time to load before rendering the PDF
    await waitForImagesToLoad(container);
    await wait(1500);

    const pdfFileName = `${fileName || 'worksheet'}.pdf`;

    if (isNative()) {
      // On iOS/Android WebView, anchor-based downloads don't work.
      // Generate a base64 blob and hand it to the native share sheet.
      const base64 = await html2pdfInstance()
        .set({
          margin: [12, 10, 12, 10],
          filename: pdfFileName,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        })
        .from(container)
        .outputPdf('datauristring');

      // Write to a temp file then open the native share sheet
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const { Share } = await import('@capacitor/share');

      const b64data = base64.split(',')[1];
      const savedFile = await Filesystem.writeFile({
        path: pdfFileName,
        data: b64data,
        directory: Directory.Cache,
      });

      await Share.share({
        title: options.title || 'Worksheet',
        url: savedFile.uri,
        dialogTitle: 'Save or share worksheet PDF',
      });
    } else {
      await html2pdfInstance()
        .set({
          margin: [12, 10, 12, 10],
          filename: pdfFileName,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] },
        })
        .from(container)
        .save();
    }
  } finally {
    if (document.body.contains(wrapper)) {
      document.body.removeChild(wrapper);
    }
  }
};

export default {
  buildWorksheetHTML,
  printWorksheet,
  saveWorksheetAsPDF,
};